<template>
  <q-dialog v-model="open" class="c-modal-stack" persistent @hide="onHide">
    <q-card class="cms-card" :style="{ maxWidth }">
      <!-- Viewport height morphs to the active sheet; sheets slide within it. -->
      <div ref="viewport" class="cms-viewport">
        <slot />
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, shallowReactive } from "vue";
import { MODAL_SIZES } from "shared/constants";
import type { ModalSize } from "shared/constants/modal.constants";
import { provideModalStack } from "./useModalStack";
import type {
  ModalStackApi,
  SheetEntry,
  TransitionDirection,
} from "./modalStack.types";

const open = defineModel<boolean>("open", { default: false });

const props = defineProps<{
  initial: string;
  // Veto hook: return false to keep the modal open (e.g. the host asked the
  // user to confirm discarding unsaved changes and they chose to keep editing).
  beforeClose?: () => boolean | Promise<boolean>;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

// Sheets register themselves so the stack can size the card (per-sheet size)
// and measure the target height for the morph. name -> { root, size }.
// shallowReactive keeps the stored template refs intact (no unwrap), so a
// lazily-rendered sheet's element is read live via `.root.value`.
const sheets = shallowReactive<Record<string, SheetEntry>>({});

const registerSheet = (name: string, entry: SheetEntry): void => {
  sheets[name] = entry;
};

const unregisterSheet = (name: string): void => {
  delete sheets[name];
};

// Navigation state.
const activeName = ref<string>(props.initial);
const history = ref<string[]>([props.initial]);
const outgoing = ref<string | null>(null);
const direction = ref<TransitionDirection>("forward");
const animating = ref(false);

const viewport = ref<HTMLElement | null>(null);
let flipTimer: ReturnType<typeof setTimeout> | undefined;

const maxWidth = computed<string>(() => {
  const size: ModalSize = sheets[activeName.value]?.size ?? "md";

  return MODAL_SIZES[size] ?? MODAL_SIZES.md;
});

// Morph the viewport height from the outgoing sheet's height to the incoming
// sheet's, while both sheets slide across. Monotonic — no dip.
const runTransition = async (fromHeight: number): Promise<void> => {
  animating.value = false;

  await nextTick();

  const el = viewport.value;

  if (!el) {
    return;
  }

  el.style.height = `${fromHeight}px`; // pin the outgoing height
  el.getBoundingClientRect(); // commit the pin + the sheets' start transforms

  const target = sheets[activeName.value]?.root?.value ?? null;
  const toHeight = target ? target.offsetHeight : fromHeight;

  requestAnimationFrame(() => {
    animating.value = true; // enable the sheet slide + drive the height
    el.style.height = `${toHeight}px`;
  });

  clearTimeout(flipTimer);
  flipTimer = setTimeout(() => {
    animating.value = false;
    outgoing.value = null; // unmount the now off-screen sheet
    el.style.height = ""; // back to auto = the active sheet's height
  }, 320);
};

const startTransition = (name: string, dir: TransitionDirection): void => {
  const fromHeight = viewport.value?.offsetHeight ?? 0;

  outgoing.value = activeName.value;
  direction.value = dir;
  activeName.value = name;

  runTransition(fromHeight);
};

const push = (name: string): void => {
  if (name === activeName.value || !sheets[name]) {
    return;
  }

  history.value.push(name);
  startTransition(name, "forward");
};

const back = (): void => {
  if (history.value.length <= 1) {
    return;
  }

  history.value.pop();
  startTransition(history.value[history.value.length - 1], "back");
};

const close = async (): Promise<void> => {
  if (props.beforeClose && (await props.beforeClose()) === false) {
    return;
  }

  open.value = false;
};

const canBack = computed<boolean>(() => history.value.length > 1);

const onHide = (): void => {
  emit("close");

  // Reset so the next open starts at the initial sheet.
  history.value = [props.initial];
  activeName.value = props.initial;
  outgoing.value = null;
  animating.value = false;
};

provideModalStack({
  activeName,
  outgoing,
  direction,
  animating,
  push,
  back,
  close,
  canBack,
  registerSheet,
  unregisterSheet,
} satisfies ModalStackApi);
</script>

<style lang="scss" scoped>
.cms-card {
  @apply tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-bg-white;
  border-radius: 1rem;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  max-height: 90vh;
  transition: max-width 300ms ease-in-out;
}

.cms-viewport {
  @apply tw-relative tw-overflow-hidden;
  transition: height 300ms ease-in-out;
}
</style>
